import { HttpClient } from '@angular/common/http';
import { Component, EventEmitter, Input, Output, SimpleChanges, ViewChild, ElementRef } from '@angular/core';
import { environment } from "@env/environment";
import { catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { MessageService } from 'primeng/api';
import { nombreLegibleDeUrl } from '@data/utils/descarga-archivo.util';

export interface DocumentState {
  isEmpty: boolean;
  isUploading: boolean;
  hasFile: boolean;
  fileName?: string;
  fileSize?: number;
  uploadDate?: Date;
  uploadProgress?: number;
}

interface ApiResponse {
  error: boolean;
  msg: string;
  data?: any;
}

@Component({
  selector: 'app-input-single-file',
  templateUrl: './input-single-file.component.html',
  styleUrls: ['./input-single-file.component.scss'],
  providers: [MessageService]
})
export class InputSingleFileComponent {
  @Input() documentName = '';
  @Input() tooltip = 'Seleccionar archivo';
  @Input() folder = '';
  @Input() label = '';
  @Input() id = '';
  @Input() fileUrlValue: string | null = null;
  @Input() fileSize: number | null = null; // Nuevo: tamaño del archivo desde el padre
  @Input() uploadDate: string | null = null; // Nuevo: fecha de subida desde el padre
  @Input() disabled = false;
  @Input() acceptedFormats = "image/*,.pdf,.docx,.xlsx,.txt,.csv,.xml";
  @Input() maxFileSize = 100 * 1024 * 1024; // 100MB
  
  @Output() fileUrl = new EventEmitter<string>();
  @Output() fileDeleted = new EventEmitter<void>();
  @Output() downloadRequested = new EventEmitter<string>();
  
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  
  file: File | null = null;
  isDragOver = false;
  documentState: DocumentState = {
    isEmpty: true,
    isUploading: false,
    hasFile: false
  };

  previewVisible = false;
  previewFileUrl: string | null = null;
  previewFileName: string | null = null;

  constructor(
    private http: HttpClient,
    private messageService: MessageService
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['fileUrlValue']) {
      this.updateDocumentState();
    }
  }

  private updateDocumentState(): void {
    if (this.fileUrlValue) {
      const fileName = this.getFileNameFromUrl(this.fileUrlValue);
      this.file = new File([new Blob()], fileName);
      this.documentState = {
        isEmpty: false,
        isUploading: false,
        hasFile: true,
        fileName: fileName,
        fileSize: this.fileSize || undefined, // Usar tamaño del padre si está disponible
        uploadDate: this.uploadDate ? new Date(this.uploadDate) : undefined // Usar fecha del padre si está disponible
      };
    } else {
      this.file = null;
      this.documentState = {
        isEmpty: true,
        isUploading: false,
        hasFile: false
      };
    }
  }

  getFileNameFromUrl(url: string): string {
    return nombreLegibleDeUrl(url);
  }

  onPreview(): void {
    if (!this.fileUrlValue) return;
    this.previewFileUrl = this.fileUrlValue;
    this.previewFileName = this.getFileNameFromUrl(this.fileUrlValue);
    this.previewVisible = true;
  }

  /**
   * Abrir el explorador de archivos
   */
  triggerFileInput(): void {
    if (this.disabled) return;
    this.fileInput.nativeElement.click();
  }

  /**
   * Manejar selección de archivo desde input
   */
  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const selectedFile = input.files[0];
      this.processFile(selectedFile);
      // Limpiar el input para permitir seleccionar el mismo archivo de nuevo
      input.value = '';
    }
  }

  /**
   * Manejar drag over
   */
  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (!this.disabled) {
      this.isDragOver = true;
    }
  }

  /**
   * Manejar drag leave
   */
  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  /**
   * Manejar drop de archivos
   */
  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;

    if (this.disabled) return;

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      const selectedFile = files[0];
      this.processFile(selectedFile);
    }
  }

  /**
   * Procesar archivo seleccionado
   */
  private processFile(selectedFile: File): void {
    // Validar tipo de archivo
    if (!this.isValidFileType(selectedFile)) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Tipo de archivo no permitido. Solo se permiten: PDF, DOCX, XLSX, JPG, PNG, GIF, WEBP, TXT, CSV, XML.'
      });
      return;
    }

    // Validación de tamaño
    if (selectedFile.size > this.maxFileSize) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: `El archivo excede el tamaño máximo permitido (${Math.round(this.maxFileSize / (1024 * 1024))}MB).`
      });
      return;
    }

    this.file = selectedFile;
    this.documentState = {
      isEmpty: false,
      isUploading: true,
      hasFile: false,
      fileName: selectedFile.name,
      fileSize: selectedFile.size,
      uploadProgress: 0
    };

    this.uploadFile();
  }

  /**
   * Validar tipo de archivo
   */
  private isValidFileType(file: File): boolean {
    const allowedTypes = [
      'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp',
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/xml',
      'text/xml',
      'text/plain',
      'text/csv'
    ];

    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    const allowedExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'pdf', 'docx', 'xlsx', 'txt', 'csv', 'xml'];

    return allowedTypes.includes(file.type) || (fileExtension ? allowedExtensions.includes(fileExtension) : false);
  }

  private uploadFile(): void {
    if (!this.file) return;

    const formData = new FormData();
    formData.append('file', this.file);
    formData.append('id', this.id);
    formData.append('documentName', this.documentName);
    formData.append('folderName', this.folder);

    const uploadUrl = `${environment.apiUrl}documents/upload`;

    // Simular progreso de subida
    const progressInterval = setInterval(() => {
      if (this.documentState.uploadProgress! < 90) {
        this.documentState.uploadProgress! += 10;
      }
    }, 200);

    this.http.post<ApiResponse>(uploadUrl, formData)
      .subscribe({
        next: response => {
          clearInterval(progressInterval);

          if (response && response.data && response.data.fileUrl) {
            this.documentState = {
              isEmpty: false,
              isUploading: false,
              hasFile: true,
              fileName: this.file!.name,
              fileSize: this.file!.size,
              uploadDate: new Date(),
              uploadProgress: 100
            };

            this.fileUrlValue = response.data.fileUrl;
            this.fileUrl.emit(response.data.fileUrl);
          } else {
            this.documentState.isUploading = false;
            this.documentState.isEmpty = true;
            this.messageService.add({
              severity: 'error',
              summary: 'Error al subir',
              detail: response?.msg || 'No se pudo subir el archivo.'
            });
          }
        },
        error: (err) => {
          clearInterval(progressInterval);
          this.documentState.isUploading = false;
          this.documentState.isEmpty = true;
          this.messageService.add({
            severity: 'error',
            summary: 'Error al subir',
            detail: err?.error?.msg || err?.message || 'No se pudo subir el archivo.'
          });
        }
      });
  }

  downloadFile(): void {
    if (!this.fileUrlValue) return;

    // Solo emitir el evento, no ejecutar la descarga aquí para evitar duplicación
    this.downloadRequested.emit(this.fileUrlValue);
  }

  deleteFile(): void {
    if (!this.fileUrlValue) return;

    // Mostrar confirmación
    this.messageService.clear();
    this.messageService.add({
      key: 'confirm-delete',
      sticky: true,
      severity: 'warn',
      summary: 'Confirmar eliminación',
      detail: '¿Estás seguro de que deseas eliminar este archivo?'
    });
  }

  confirmDelete(): void {
    if (!this.fileUrlValue) return;

    const encodedFileUrl = encodeURIComponent(this.fileUrlValue);
    const deleteUrl = `${environment.apiUrl}documents/delete?fileUrl=${encodedFileUrl}`;

    // Eliminar archivo por URL usando el endpoint correcto
    this.http.delete(deleteUrl)
      .pipe(
        catchError(() => {
          this.messageService.clear();
          return of(null);
        })
      )
      .subscribe(response => {
        if (response !== null) {
          // Actualizar estado del componente
          this.fileUrlValue = null;
          this.file = null;
          this.documentState = {
            isEmpty: true,
            isUploading: false,
            hasFile: false
          };
          
          // Notificar al componente padre
          this.fileDeleted.emit();
          
          // Limpiar mensajes de confirmación
          this.messageService.clear();
        } else {
          this.messageService.clear();
        }
      });
  }

  cancelDelete(): void {
    this.messageService.clear();
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  getFileIcon(fileName: string): string {
    const extension = fileName.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'pdf':
        return 'fa-file-pdf';
      case 'doc':
      case 'docx':
        return 'fa-file-word';
      case 'xls':
      case 'xlsx':
        return 'fa-file-excel';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'webp':
        return 'fa-file-image';
      case 'txt':
      case 'csv':
        return 'fa-file-lines';
      default:
        return 'fa-file';
    }
  }

  /**
   * Truncar nombre de archivo para evitar overflow
   */
  getTruncatedFileName(fileName: string, maxLength: number = 25): string {
    if (fileName.length <= maxLength) {
      return fileName;
    }
    
    const extension = fileName.split('.').pop();
    const nameWithoutExtension = fileName.substring(0, fileName.lastIndexOf('.'));
    const maxNameLength = maxLength - (extension ? extension.length + 4 : 3); // Espacio para "..." y extensión
    
    if (extension) {
      return `${nameWithoutExtension.substring(0, maxNameLength)}...${extension}`;
    } else {
      return `${fileName.substring(0, maxLength - 3)}...`;
    }
  }
}
